/**
 * Real-time updates hook using Firestore listeners
 */

import {useEffect} from 'react';
import {collection, query, where, onSnapshot, doc} from 'firebase/firestore';
import {getFirestoreInstance, useAuthStore, useMeetingsStore, type Meeting} from '@voxapilot/shared';

/**
 * Hook to listen for meeting changes in real-time
 */
export function useRealtimeMeetings() {
  const {user} = useAuthStore();
  const {updateMeeting, removeMeeting} = useMeetingsStore();

  useEffect(() => {
    if (!user) return;

    const db = getFirestoreInstance();
    const meetingsRef = collection(db, 'meetings');
    const q = query(meetingsRef, where('userId', '==', user.uid));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const meetingData = {id: change.doc.id, ...change.doc.data()};

          if (change.type === 'modified') {
            updateMeeting(change.doc.id, meetingData);
          } else if (change.type === 'removed') {
            removeMeeting(change.doc.id);
          }
        });
      },
      (error) => {
        console.error('Error listening to meetings:', error);
      }
    );

    return () => unsubscribe();
  }, [user, updateMeeting, removeMeeting]);
}

/**
 * Hook to listen for a specific meeting's changes
 */
export function useRealtimeMeeting(meetingId: string) {
  const {user} = useAuthStore();
  const {setCurrentMeeting} = useMeetingsStore();

  useEffect(() => {
    if (!user || !meetingId) return;

    const db = getFirestoreInstance();
    const meetingRef = doc(db, 'meetings', meetingId);

    const unsubscribe = onSnapshot(
      meetingRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const meetingData = {id: snapshot.id, ...snapshot.data()} as Meeting;
          setCurrentMeeting(meetingData);
        }
      },
      (error) => {
        console.error('Error listening to meeting:', error);
      }
    );

    return () => unsubscribe();
  }, [user, meetingId, setCurrentMeeting]);
}

